import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { ServicesURL, downloadFile, uploadFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionsType } from "src/interfaces/Common";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { KeyURL, MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { axiosInstance, fetcher } from "src/services/config";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import ButtonBackPage from "@components/ButtonBackPage";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import { usePostUploadDetail } from "@hooks/useFetchDetail";
import { AxiosResponse } from "axios";
import { useFilters } from "@context/filters";

const Subscribed: React.FC = () => {
  const { id = "1", idEdition } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.teachersArea.my-classes-subscribed"]}`;

  const navigate = useNavigate();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [participantFileData, setParticipantFileData] = useState<{
    url?: string;
    name?: string;
  }>({});

  useEffect(() => {
    getParticipantFileData();
  }, []);

  const { dataTable, columns, actionForRow, actionsTopToolbar, mutate } =
    useFetchTable(
      `${ServicesURL.getEnrollEditionDetail}${`?editionId=${idEdition}&corporateId=${id}`}`,
      PagesID["corporate.teachersArea.my-classes-subscribed"],
      filters[filterId],
    );

  const getBase64 = (file: any) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const getParticipantFileData = () => {
    const urlDownloadPaticipants: any = ServicesURL["downloadParticipants"];
    axiosInstance
      .get(
        urlDownloadPaticipants
          .replace("{userEid}", user?.id_user)
          .replace("{editionId}", idEdition),
        {
          headers: {
            accept: "application/zip",
          },
          responseType: "blob",
        },
      )
      .then((res: AxiosResponse) => {
        let downloadUrl;
        let filename;

        if (res.data.public_url) {
          downloadUrl = res.data.public_url;
        } else if (res.config.url?.startsWith("http")) {
          downloadUrl = res.config.url;
          filename = res.config.url;
        } else {
          filename =
            res.headers["content-disposition"]?.split("filename=")[1] || "file";
          const type = res.headers["content-type"];
          const blob = new Blob([res.data], { type: type });
          downloadUrl = URL.createObjectURL(blob);
        }

        setParticipantFileData({
          url: downloadUrl,
          name: filename,
        });
      })
      .catch((error) => console.error(error));
  };

  const downloadParticipantFile = () => {
    const link = document.createElement("a");
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.href = participantFileData.url || "";
    link.download = participantFileData.name || "";
    link.click();
    document.body.removeChild(link);
  };

  const { trigger: triggerUploadParticipant } = usePostUploadDetail(
    ServicesURL.uploadParticipants +
      `/${user?.id_user}/attendanceReportEdition/${idEdition}/${id}`,
    {
      onError: () => {},
      onSuccess: (data: any) => {
        getParticipantFileData();
      },
    },
  );

  const { t } = useTranslation(["teachers-area-my-classes-subscribed-page"]);

  const actionsRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "link": {
          return {
            ...action,
            callBack: (obj: any) => {
              navigate(`${obj.idUser}/revisions`);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "upload": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: ServicesURL.postWrapper,
              types: ["upload", "download", "template"],
              onDownload: (el) => {
                if (!!el) {
                  const urlDownloadTemplatePaticipants: any =
                    ServicesURL["downloadTemplateParticipants"];
                  downloadFile(
                    urlDownloadTemplatePaticipants
                      .replace("{userEid}", user?.id_user)
                      .replace("{corporateId}", id)
                      .replace("{editionId}", idEdition),
                    false,
                    "",
                    {
                      headers: {
                        accept: "text/csv",
                      },
                    },
                  );
                } else {
                  if (!participantFileData.url) return;
                  downloadParticipantFile();
                }
              },
              onUpload: async (formData: any) => {
                const file = formData.get("file");

                return await getBase64(formData.get("file")).then(
                  (newFile: any) => {
                    const formattedParams: any = {
                      contentFile:
                        newFile?.split(",") && newFile?.split(",")[1],
                      nomeFile: file.name.split(".csv")[0],
                      tipoFile:
                        file.type.split("/") && `.${file.type.split("/")[1]}`,
                    };

                    const response = triggerUploadParticipant(formattedParams);

                    return response;
                  },
                );
              },
            },
          };
          return {
            ...action,
            ...{
              callBack: () =>
                setOpenUpload({
                  ...actionModal,
                  downloadDisabled: !!participantFileData.url,
                }),
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-my-classes.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["corporate.teachersArea.my-classes-subscribed"]}
            data={dataTable?.users || []}
            columns={columns || []}
            configurationTopToolbar={actionsToolbar}
            configurationActionForRow={actionsRow}
            totalPages={dataTable?.metadata?.totalElements}
          />
        </Box>
      </Container>
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
      />
    </AnimationFadeIn>
  );
};

export default Subscribed;
